import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_CONFIG, fetchAPI } from '@/services/api';

export function SetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = useMemo(() => params.get('email') || '', [params]);
  const token = useMemo(() => params.get('token') || '', [params]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email || !token) {
      setError('Invalid or missing reset link.');
      return;
    }
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetchAPI<any>(`${API_CONFIG.USERS_API}`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'password_reset_confirm',
          email,
          token,
          newPassword: password,
        }),
      });
      if (!res?.success) {
        setError(res?.error || 'Unable to set password.');
        return;
      }
      setSuccess('Password set. You can log in now.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err: any) {
      setError(err?.message || 'Unable to set password.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="set-password-page min-h-screen px-6 py-12 text-white bg-dark-bg flex items-center justify-center">
      <div className="auth-surface w-full max-w-md bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
        <h1 className="text-2xl font-semibold">Create Your Password</h1>
        <p className="text-gray-400 text-sm">
          Set a new password for <span className="text-white">{email || 'your account'}</span>.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
              placeholder="Min 6 characters"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Confirm Password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
              required
            />
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}
          {success && <p className="text-green-400 text-sm">{success}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving…' : 'Set Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
